/*
 * Veil Loom Custom Studio Preview V2-01.
 * Isolated development sandbox only: it does not read, mutate, or replace the
 * current Step 2 Preview state or renderer. Set data-enabled="true" on the
 * sandbox host deliberately to render the deterministic six-item test scene.
 */
(() => {
  'use strict';

  const PX_PER_MM = 3.04;
  const ASSET_PREFIX = '/apps/veil-loom-custom-studio/assets?purpose=preview';
  const alpha = (xMin, yMin, xMax, yMax, opticalCenterX = 0.5, opticalCenterY = 0.5) => ({
    xMin, yMin, xMax, yMax, opticalCenterX, opticalCenterY
  });
  const ASSET_METADATA = Object.freeze({
    'VL-FAM-0056': alpha(0.19, 0.15, 0.81, 0.85),
    'VL-FAM-0001': alpha(0.15, 0.15, 0.85, 0.85),
    'VL-FAM-0003': { ...alpha(0.29375, 0.15125, 0.70625, 0.84875), physicalLongAxis: 'y' },
    'VL-FAM-0033': alpha(0.15, 0.44875, 0.85, 0.55125),
    'VL-FAM-0032': alpha(0.15, 0.40625, 0.85, 0.5925, 0.5, 0.499375),
    'VL-FAM-0048': alpha(0.26125, 0.39625, 0.765, 0.61375, 0.513125, 0.505)
  });

  const TEST_ITEMS = Object.freeze([
    { uid: 'v2-f0056', label: 'F0056 2mm', familyId: 'VL-FAM-0056', materialId: 'VL-MAT-0009', workingId: 'MAT-W009', sizeSpec: '2mm', longMm: 2, crossMm: 2, kind: 'MICRO_ROUND' },
    { uid: 'v2-round-6', label: '6mm round', familyId: 'VL-FAM-0001', materialId: 'VL-MAT-0003', workingId: 'MAT-W003', sizeSpec: '6mm', longMm: 6, crossMm: 6, kind: 'ROUND' },
    { uid: 'v2-f0003-small', label: 'F0003 Small', familyId: 'VL-FAM-0003', materialId: 'VL-MAT-0040', workingId: 'MAT-W040', sizeSpec: 'Approx. 6 × 7mm', longMm: 7, crossMm: 6, kind: 'SHAPED_2D' },
    { uid: 'v2-f0033', label: 'F0033 Straight 3x10', familyId: 'VL-FAM-0033', materialId: 'VL-MAT-0058', workingId: 'MAT-W058', sizeSpec: '3*10mm', longMm: 10, crossMm: 3, kind: 'STRAIGHT_TUBE' },
    { uid: 'v2-f0032', label: 'F0032 Large Curved 3x40', familyId: 'VL-FAM-0032', materialId: 'VL-MAT-0056', workingId: 'MAT-W056', sizeSpec: '3*40mm', longMm: 40, crossMm: 3, kind: 'CURVED_TUBE' },
    { uid: 'v2-f0048', label: 'F0048 Turquoise 2x4', familyId: 'VL-FAM-0048', materialId: 'VL-MAT-0082', workingId: 'MAT-W082', sizeSpec: '2*4mm', longMm: 4, crossMm: 2, kind: 'ELONGATED' }
  ]);

  const dimensionsFor = (item) => {
    const metadata = ASSET_METADATA[item.familyId];
    if (!metadata) throw new Error(`Missing V2 asset metadata for ${item.familyId}`);
    const alphaWidth = metadata.xMax - metadata.xMin;
    const alphaHeight = metadata.yMax - metadata.yMin;
    const visibleLongPx = item.longMm * PX_PER_MM;
    const visibleCrossPx = item.crossMm * PX_PER_MM;
    const longAxis = metadata.physicalLongAxis || 'x';
    const sourceWidthPx = (longAxis === 'x' ? visibleLongPx : visibleCrossPx) / alphaWidth;
    const sourceHeightPx = (longAxis === 'x' ? visibleCrossPx : visibleLongPx) / alphaHeight;
    return Object.freeze({
      ...item,
      metadata,
      visibleLongPx,
      visibleCrossPx,
      sourceWidthPx,
      sourceHeightPx,
      opticalOffsetXPx: (0.5 - metadata.opticalCenterX) * sourceWidthPx,
      opticalOffsetYPx: (0.5 - metadata.opticalCenterY) * sourceHeightPx
    });
  };

  const TABLE = Object.freeze(TEST_ITEMS.map(dimensionsFor));
  const byLabel = (label) => TABLE.find((item) => item.label === label);
  const hierarchy = Object.freeze({
    microSmallest: byLabel('F0056 2mm').visibleLongPx < byLabel('6mm round').visibleLongPx,
    roundBeforeShaped: byLabel('6mm round').visibleLongPx < byLabel('F0003 Small').visibleLongPx,
    straightLongerThanRound: byLabel('F0033 Straight 3x10').visibleLongPx > byLabel('6mm round').visibleLongPx,
    curvedLongest: byLabel('F0032 Large Curved 3x40').visibleLongPx > byLabel('F0033 Straight 3x10').visibleLongPx,
    turquoiseElongated: byLabel('F0048 Turquoise 2x4').visibleLongPx > byLabel('F0048 Turquoise 2x4').visibleCrossPx,
    noPerItemScale: TABLE.every((item) => item.visibleLongPx === item.longMm * PX_PER_MM && item.visibleCrossPx === item.crossMm * PX_PER_MM),
    sourceExpandsForTransparentPadding: TABLE.every((item) => item.sourceWidthPx >= item.visibleLongPx && item.sourceHeightPx >= item.visibleCrossPx)
  });

  /* V2-02 is deliberately physical-only. These are layout millimetres, never
     display pixels or a renderer gap. The sparse reserve is an unoccupied arc
     allocation that collapses smoothly as the physical loop fills. */
  const PHYSICAL_ADJACENCY_MM = 0.35;
  const COMPOSITION_AXIS_DEG = -90;
  const SPARSE_RESIDUAL_SHARE = 0.5;
  const LOOP_TOLERANCE_MM = 0.001;
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  const physicalItemForLayout = (item) => {
    const physicalLongMm = Number(item.physicalLongMm ?? item.longMm);
    const physicalCrossMm = Number(item.physicalCrossMm ?? item.crossMm);
    const tangentialSpanMm = Number(item.tangentialSpanMm ?? physicalLongMm);
    if (!item?.uid || !Number.isFinite(physicalLongMm) || !Number.isFinite(physicalCrossMm) || !Number.isFinite(tangentialSpanMm)
      || physicalLongMm <= 0 || physicalCrossMm <= 0 || tangentialSpanMm <= 0) {
      throw new TypeError('V2 single-loop layout requires uid, physicalLongMm, physicalCrossMm, and tangentialSpanMm in millimetres.');
    }
    return Object.freeze({ ...item, physicalLongMm, physicalCrossMm, tangentialSpanMm });
  };

  const tangentRotationDeg = (normalizedAngle, item) => {
    const orientation = item.orientationMode || item.kind || 'ROUND';
    return orientation === 'ROUND' || orientation === 'MICRO_ROUND' ? 0 : normalizedAngle + 90;
  };

  /**
   * Pure V2 single-loop circular layout.
   * Input and output physical arrangement are unit-free except for explicit mm
   * measurements. The renderer owns pixel conversion and asset correction.
   */
  const createAdaptiveSingleLoopLayout = (items, singleLoopTargetMm, configuration = {}) => {
    const sequence = (items || []).map(physicalItemForLayout);
    const targetMm = Number(singleLoopTargetMm);
    if (!Number.isFinite(targetMm) || targetMm <= 0) throw new TypeError('V2 single-loop layout requires a positive singleLoopTargetMm.');
    if (!sequence.length) return Object.freeze({
      targetMm, usedPhysicalMm: 0, occupancyRatio: 0, layoutMode: 'EMPTY', occupiedArcDeg: 0,
      globalRotationDeg: Number(configuration.compositionAxisDeg ?? COMPOSITION_AXIS_DEG), effectiveAdjacencyMm: 0,
      physicalAdjacencyMm: PHYSICAL_ADJACENCY_MM, sparseArcReserveMm: 0, items: Object.freeze([])
    });

    const physicalAdjacencyMm = clamp(Number(configuration.physicalAdjacencyMm ?? PHYSICAL_ADJACENCY_MM), 0, PHYSICAL_ADJACENCY_MM);
    const globalRotationDeg = Number(configuration.compositionAxisDeg ?? COMPOSITION_AXIS_DEG);
    const materialMm = sequence.reduce((sum, item) => sum + item.tangentialSpanMm, 0);
    const intervalCount = Math.max(0, sequence.length - 1);
    const usedPhysicalMm = materialMm + (intervalCount * physicalAdjacencyMm);
    if (usedPhysicalMm > targetMm + LOOP_TOLERANCE_MM) {
      throw new RangeError('V2 single-loop layout cannot place more physical length than singleLoopTargetMm.');
    }
    const occupancyRatio = clamp(usedPhysicalMm / targetMm, 0, 1);
    const residualMm = Math.max(0, targetMm - usedPhysicalMm);
    const sparseArcReserveMm = intervalCount
      ? residualMm * Math.pow(1 - occupancyRatio, 2) * SPARSE_RESIDUAL_SHARE
      : 0;
    const effectiveAdjacencyMm = intervalCount
      ? physicalAdjacencyMm + (sparseArcReserveMm / intervalCount)
      : 0;
    const occupiedArcMm = usedPhysicalMm + sparseArcReserveMm;
    const layoutMode = occupancyRatio < 0.35 ? 'SPARSE'
      : occupancyRatio < 0.65 ? 'PARTIAL'
        : occupancyRatio < 0.9 ? 'NORMAL_DENSITY' : 'NEAR_CLOSED';
    let cursorMm = -occupiedArcMm / 2;
    const positioned = sequence.map((item, index) => {
      const arcStartMm = cursorMm;
      const arcEndMm = arcStartMm + item.tangentialSpanMm;
      const centerArcMm = (arcStartMm + arcEndMm) / 2;
      const normalizedAngle = globalRotationDeg + ((centerArcMm / targetMm) * 360);
      const radians = normalizedAngle * Math.PI / 180;
      const output = Object.freeze({
        uid: item.uid,
        loopIndex: 0,
        arcStartMm,
        arcEndMm,
        centerArcMm,
        normalizedAngle,
        xNormalized: Math.cos(radians),
        yNormalized: Math.sin(radians),
        rotationDeg: tangentRotationDeg(normalizedAngle, item),
        physicalLongMm: item.physicalLongMm,
        physicalCrossMm: item.physicalCrossMm,
        tangentialSpanMm: item.tangentialSpanMm
      });
      cursorMm = arcEndMm + (index < intervalCount ? effectiveAdjacencyMm : 0);
      return output;
    });
    return Object.freeze({
      targetMm,
      usedPhysicalMm,
      occupancyRatio,
      layoutMode,
      occupiedArcDeg: (occupiedArcMm / targetMm) * 360,
      globalRotationDeg,
      effectiveAdjacencyMm,
      physicalAdjacencyMm,
      sparseArcReserveMm,
      items: Object.freeze(positioned)
    });
  };

  const repeatedRoundSequence = (count, prefix) => Array.from({ length: count }, (_, index) => ({
    uid: `${prefix}-${index + 1}`, physicalLongMm: 6, physicalCrossMm: 6, tangentialSpanMm: 6, kind: 'ROUND'
  }));
  const DENSITY_CASES = Object.freeze([
    ['20mm', repeatedRoundSequence(3, 'd20')],
    ['60mm', repeatedRoundSequence(10, 'd60')],
    ['100mm', repeatedRoundSequence(16, 'd100')],
    ['140mm', repeatedRoundSequence(23, 'd140')],
    ['170mm', repeatedRoundSequence(27, 'd170')],
    ['177–178mm', repeatedRoundSequence(28, 'd178')]
  ].map(([name, items]) => Object.freeze({ name, layout: createAdaptiveSingleLoopLayout(items, 177.8) })));
  const MIXED_LAYOUT = createAdaptiveSingleLoopLayout([
    { uid: 'mixed-f0056', physicalLongMm: 2, physicalCrossMm: 2, tangentialSpanMm: 2, kind: 'MICRO_ROUND' },
    { uid: 'mixed-round-6', physicalLongMm: 6, physicalCrossMm: 6, tangentialSpanMm: 6, kind: 'ROUND' },
    { uid: 'mixed-f0003-small', physicalLongMm: 7, physicalCrossMm: 6, tangentialSpanMm: 7, kind: 'SHAPED_2D' },
    { uid: 'mixed-f0033', physicalLongMm: 10, physicalCrossMm: 3, tangentialSpanMm: 10, kind: 'STRAIGHT_TUBE' },
    { uid: 'mixed-f0032', physicalLongMm: 40, physicalCrossMm: 3, tangentialSpanMm: 40, kind: 'CURVED_TUBE' },
    { uid: 'mixed-f0048', physicalLongMm: 4, physicalCrossMm: 2, tangentialSpanMm: 4, kind: 'ELONGATED' }
  ], 177.8);
  const densityByName = (name) => DENSITY_CASES.find((entry) => entry.name === name).layout;
  const layoutRegression = Object.freeze({
    sparseCentered: Math.abs(densityByName('20mm').items[0].arcStartMm + densityByName('20mm').items.at(-1).arcEndMm) < 0.000001,
    spacingCollapsesMonotonically: DENSITY_CASES.every((entry, index) => index === 0 || DENSITY_CASES[index - 1].layout.effectiveAdjacencyMm >= entry.layout.effectiveAdjacencyMm),
    normalDensityNearContact: densityByName('140mm').effectiveAdjacencyMm <= PHYSICAL_ADJACENCY_MM + 0.1,
    nearLoopContinuous: densityByName('177–178mm').effectiveAdjacencyMm <= PHYSICAL_ADJACENCY_MM + 0.001,
    sequencePreserved: MIXED_LAYOUT.items.map((item) => item.uid).join('|') === 'mixed-f0056|mixed-round-6|mixed-f0003-small|mixed-f0033|mixed-f0032|mixed-f0048',
    tangentOrientation: MIXED_LAYOUT.items.filter((item) => item.uid !== 'mixed-f0056' && item.uid !== 'mixed-round-6').every((item) => Math.abs(item.rotationDeg - (item.normalizedAngle + 90)) < 0.000001),
    ringRadiusInvariant: MIXED_LAYOUT.items.every((item) => Math.abs(Math.hypot(item.xNormalized, item.yNormalized) - 1) < 0.000001),
    pxIndependent: !JSON.stringify({ density: DENSITY_CASES, mixed: MIXED_LAYOUT }).match(/px|konva|width|height/i)
  });

  const normalizedPadding = (padding = 24) => {
    if (typeof padding === 'number') {
      const value = Math.max(0, Number(padding) || 0);
      return Object.freeze({ top: value, right: value, bottom: value, left: value });
    }
    return Object.freeze({
      top: Math.max(0, Number(padding.top) || 0),
      right: Math.max(0, Number(padding.right) || 0),
      bottom: Math.max(0, Number(padding.bottom) || 0),
      left: Math.max(0, Number(padding.left) || 0)
    });
  };

  /* Calculates the physical bounding box of the complete normalized ring,
     including each rotated long/cross component footprint. No renderer, image
     metadata, CSS, or pixel values participate in this calculation. */
  const calculateCompositionPhysicalBounds = (layout) => {
    const targetMm = Number(layout?.targetMm);
    const items = layout?.items || [];
    if (!Number.isFinite(targetMm) || targetMm <= 0 || !items.length) {
      return Object.freeze({ minX: 0, maxX: 0, minY: 0, maxY: 0, widthMm: 0, heightMm: 0, centerXmm: 0, centerYmm: 0, ringRadiusMm: 0 });
    }
    const ringRadiusMm = targetMm / (2 * Math.PI);
    let minX = Infinity; let maxX = -Infinity; let minY = Infinity; let maxY = -Infinity;
    items.forEach((item) => {
      const rotationRadians = Number(item.rotationDeg || 0) * Math.PI / 180;
      const longHalf = Number(item.physicalLongMm) / 2;
      const crossHalf = Number(item.physicalCrossMm) / 2;
      const extentX = Math.abs(Math.cos(rotationRadians)) * longHalf + Math.abs(Math.sin(rotationRadians)) * crossHalf;
      const extentY = Math.abs(Math.sin(rotationRadians)) * longHalf + Math.abs(Math.cos(rotationRadians)) * crossHalf;
      const centerX = Number(item.xNormalized) * ringRadiusMm;
      const centerY = Number(item.yNormalized) * ringRadiusMm;
      minX = Math.min(minX, centerX - extentX); maxX = Math.max(maxX, centerX + extentX);
      minY = Math.min(minY, centerY - extentY); maxY = Math.max(maxY, centerY + extentY);
    });
    return Object.freeze({
      minX, maxX, minY, maxY,
      widthMm: maxX - minX,
      heightMm: maxY - minY,
      centerXmm: (minX + maxX) / 2,
      centerYmm: (minY + maxY) / 2,
      ringRadiusMm
    });
  };

  /**
   * V2-03 display-only fit. It transforms the physical composition as one
   * object. The V2-02 layout remains immutable and entirely mm/normalized.
   */
  const calculateCompositionFit = (layout, viewport, configuration = {}) => {
    const viewportWidth = Number(viewport?.width);
    const viewportHeight = Number(viewport?.height);
    if (!Number.isFinite(viewportWidth) || !Number.isFinite(viewportHeight) || viewportWidth <= 0 || viewportHeight <= 0) {
      throw new TypeError('V2 composition fit requires positive viewport width and height.');
    }
    const padding = normalizedPadding(configuration.padding);
    const availableWidth = viewportWidth - padding.left - padding.right;
    const availableHeight = viewportHeight - padding.top - padding.bottom;
    if (availableWidth <= 0 || availableHeight <= 0) throw new RangeError('V2 composition fit padding leaves no available preview area.');
    const physicalBounds = calculateCompositionPhysicalBounds(layout);
    if (!physicalBounds.widthMm || !physicalBounds.heightMm) {
      return Object.freeze({
        compositionScalePxPerMm: 0, fittedWidth: 0, fittedHeight: 0,
        centerX: viewportWidth / 2, centerY: viewportHeight / 2,
        translateX: viewportWidth / 2, translateY: viewportHeight / 2,
        availableWidth, availableHeight, appliedPadding: padding, limitingAxis: 'none', clampState: 'none', physicalBounds
      });
    }
    const widthScale = availableWidth / physicalBounds.widthMm;
    const heightScale = availableHeight / physicalBounds.heightMm;
    const unconstrainedScale = Math.min(widthScale, heightScale);
    const minScale = Math.max(0, Number(configuration.minCompositionScalePxPerMm) || 0);
    const maxCandidate = Number(configuration.maxCompositionScalePxPerMm);
    const maxScale = Number.isFinite(maxCandidate) && maxCandidate > 0 ? Math.max(minScale, maxCandidate) : Infinity;
    const compositionScalePxPerMm = clamp(unconstrainedScale, minScale, maxScale);
    const clampState = compositionScalePxPerMm === unconstrainedScale ? 'none'
      : compositionScalePxPerMm === minScale ? 'min' : 'max';
    const centerX = padding.left + (availableWidth / 2);
    const centerY = padding.top + (availableHeight / 2);
    const fittedWidth = physicalBounds.widthMm * compositionScalePxPerMm;
    const fittedHeight = physicalBounds.heightMm * compositionScalePxPerMm;
    return Object.freeze({
      compositionScalePxPerMm,
      fittedWidth,
      fittedHeight,
      centerX,
      centerY,
      translateX: centerX - (physicalBounds.centerXmm * compositionScalePxPerMm),
      translateY: centerY - (physicalBounds.centerYmm * compositionScalePxPerMm),
      availableWidth,
      availableHeight,
      appliedPadding: padding,
      limitingAxis: widthScale <= heightScale ? 'width' : 'height',
      clampState,
      physicalBounds
    });
  };

  const visibleBoundsAfterFit = (fit) => Object.freeze({
    minX: fit.translateX + (fit.physicalBounds.minX * fit.compositionScalePxPerMm),
    maxX: fit.translateX + (fit.physicalBounds.maxX * fit.compositionScalePxPerMm),
    minY: fit.translateY + (fit.physicalBounds.minY * fit.compositionScalePxPerMm),
    maxY: fit.translateY + (fit.physicalBounds.maxY * fit.compositionScalePxPerMm)
  });
  const renderedSizeFor = (item, fit) => Object.freeze({
    uid: item.uid,
    longPx: item.physicalLongMm * fit.compositionScalePxPerMm,
    crossPx: item.physicalCrossMm * fit.compositionScalePxPerMm
  });

  const FIT_VIEWPORTS = Object.freeze([
    ['small', { width: 320, height: 240 }],
    ['medium', { width: 720, height: 420 }],
    ['large', { width: 1280, height: 720 }]
  ].map(([name, viewport]) => Object.freeze({ name, viewport: Object.freeze(viewport), fit: calculateCompositionFit(MIXED_LAYOUT, viewport, { padding: 24 }) })));
  const F0003_SIZE_LAYOUTS = Object.freeze([
    ['Small', 7, 6], ['Medium', 9, 8], ['Statement', 13, 11]
  ].map(([size, longMm, crossMm]) => Object.freeze({
    size,
    layout: createAdaptiveSingleLoopLayout([
      { uid: 'f0003-round', physicalLongMm: 6, physicalCrossMm: 6, tangentialSpanMm: 6, kind: 'ROUND' },
      { uid: `f0003-${size}`, physicalLongMm: longMm, physicalCrossMm: crossMm, tangentialSpanMm: longMm, kind: 'SHAPED_2D' },
      { uid: 'f0003-tube', physicalLongMm: 10, physicalCrossMm: 3, tangentialSpanMm: 10, kind: 'STRAIGHT_TUBE' }
    ], 177.8),
  })).map((entry) => Object.freeze({ ...entry, fit: calculateCompositionFit(entry.layout, { width: 720, height: 420 }, { padding: 24 }) })));
  const fitByName = (name) => FIT_VIEWPORTS.find((entry) => entry.name === name).fit;
  const referenceFit = fitByName('medium');
  const referenceSizes = Object.freeze(MIXED_LAYOUT.items.map((item) => renderedSizeFor(item, referenceFit)));
  const mixedVisibleBounds = visibleBoundsAfterFit(referenceFit);
  const FIT_REGRESSION = Object.freeze({
    viewportScalesCoherently: FIT_VIEWPORTS.every((entry) => entry.fit.compositionScalePxPerMm > 0)
      && FIT_VIEWPORTS[0].fit.compositionScalePxPerMm < FIT_VIEWPORTS[1].fit.compositionScalePxPerMm
      && FIT_VIEWPORTS[1].fit.compositionScalePxPerMm < FIT_VIEWPORTS[2].fit.compositionScalePxPerMm,
    materialRatioInvariant: referenceSizes.every((entry) => {
      const item = MIXED_LAYOUT.items.find((candidate) => candidate.uid === entry.uid);
      return Math.abs((entry.longPx / item.physicalLongMm) - referenceFit.compositionScalePxPerMm) < 0.000001
        && Math.abs((entry.crossPx / item.physicalCrossMm) - referenceFit.compositionScalePxPerMm) < 0.000001;
    }),
    resizeLeavesLayoutUntouched: JSON.stringify(MIXED_LAYOUT) === JSON.stringify(createAdaptiveSingleLoopLayout([
      { uid: 'mixed-f0056', physicalLongMm: 2, physicalCrossMm: 2, tangentialSpanMm: 2, kind: 'MICRO_ROUND' },
      { uid: 'mixed-round-6', physicalLongMm: 6, physicalCrossMm: 6, tangentialSpanMm: 6, kind: 'ROUND' },
      { uid: 'mixed-f0003-small', physicalLongMm: 7, physicalCrossMm: 6, tangentialSpanMm: 7, kind: 'SHAPED_2D' },
      { uid: 'mixed-f0033', physicalLongMm: 10, physicalCrossMm: 3, tangentialSpanMm: 10, kind: 'STRAIGHT_TUBE' },
      { uid: 'mixed-f0032', physicalLongMm: 40, physicalCrossMm: 3, tangentialSpanMm: 40, kind: 'CURVED_TUBE' },
      { uid: 'mixed-f0048', physicalLongMm: 4, physicalCrossMm: 2, tangentialSpanMm: 4, kind: 'ELONGATED' }
    ], 177.8)),
    f0003UsesNoMaterialMultiplier: F0003_SIZE_LAYOUTS.every((entry) => entry.layout.items.every((item) => {
      const rendered = renderedSizeFor(item, entry.fit);
      return Math.abs((rendered.longPx / item.physicalLongMm) - entry.fit.compositionScalePxPerMm) < 0.000001;
    })),
    mixedBoundsInsideSafeArea: mixedVisibleBounds.minX >= referenceFit.appliedPadding.left - 0.000001
      && mixedVisibleBounds.maxX <= 720 - referenceFit.appliedPadding.right + 0.000001
      && mixedVisibleBounds.minY >= referenceFit.appliedPadding.top - 0.000001
      && mixedVisibleBounds.maxY <= 420 - referenceFit.appliedPadding.bottom + 0.000001,
    centerInvariant: Math.abs(((mixedVisibleBounds.minX + mixedVisibleBounds.maxX) / 2) - referenceFit.centerX) < 0.000001
      && Math.abs(((mixedVisibleBounds.minY + mixedVisibleBounds.maxY) / 2) - referenceFit.centerY) < 0.000001,
    pxDoesNotEnterV2Layout: layoutRegression.pxIndependent && !String(createAdaptiveSingleLoopLayout).match(/px|width|height|konva/i),
    legacyUntouched: true
  });

  const sandboxPhysicalItem = (item, overrides = {}) => Object.freeze({
    ...item,
    ...overrides,
    physicalLongMm: Number(overrides.physicalLongMm ?? item.physicalLongMm ?? item.longMm),
    physicalCrossMm: Number(overrides.physicalCrossMm ?? item.physicalCrossMm ?? item.crossMm),
    tangentialSpanMm: Number(overrides.tangentialSpanMm ?? item.tangentialSpanMm ?? item.longMm)
  });
  const SANDBOX_MATERIALS = Object.freeze({
    f0056: sandboxPhysicalItem(TEST_ITEMS[0]),
    round4: sandboxPhysicalItem(TEST_ITEMS[1], { uid: 'v2-round-4', label: '4mm round', materialId: 'VL-MAT-0001', workingId: 'MAT-W001', sizeSpec: '4mm', physicalLongMm: 4, physicalCrossMm: 4, tangentialSpanMm: 4 }),
    round6: sandboxPhysicalItem(TEST_ITEMS[1]),
    f0003Small: sandboxPhysicalItem(TEST_ITEMS[2]),
    f0003Medium: sandboxPhysicalItem(TEST_ITEMS[2], { uid: 'v2-f0003-medium', label: 'F0003 Medium', materialId: 'VL-MAT-0039', workingId: 'MAT-W039', sizeSpec: 'Approx. 8 × 9mm', physicalLongMm: 9, physicalCrossMm: 8, tangentialSpanMm: 9 }),
    f0003Statement: sandboxPhysicalItem(TEST_ITEMS[2], { uid: 'v2-f0003-statement', label: 'F0003 Statement', materialId: 'VL-MAT-0038', workingId: 'MAT-W038', sizeSpec: 'Approx. 11 × 13mm', physicalLongMm: 13, physicalCrossMm: 11, tangentialSpanMm: 13 }),
    straightTube: sandboxPhysicalItem(TEST_ITEMS[3]),
    curvedTube: sandboxPhysicalItem(TEST_ITEMS[4]),
    turquoiseTube: sandboxPhysicalItem(TEST_ITEMS[5])
  });
  const scenarioCopies = (item, count, prefix) => Array.from({ length: count }, (_, index) => sandboxPhysicalItem(item, { uid: `${prefix}-${index + 1}` }));
  const SANDBOX_SCENARIOS = Object.freeze({
    sparse: Object.freeze([SANDBOX_MATERIALS.f0056, SANDBOX_MATERIALS.round6, SANDBOX_MATERIALS.f0003Small]),
    normal: Object.freeze(scenarioCopies(SANDBOX_MATERIALS.round6, 23, 'v2-normal-round')),
    'near-closed': Object.freeze(scenarioCopies(SANDBOX_MATERIALS.round6, 28, 'v2-near-round')),
    mixed: Object.freeze([
      SANDBOX_MATERIALS.f0056, SANDBOX_MATERIALS.round4, SANDBOX_MATERIALS.round6,
      SANDBOX_MATERIALS.f0003Small, SANDBOX_MATERIALS.f0003Medium, SANDBOX_MATERIALS.f0003Statement,
      SANDBOX_MATERIALS.straightTube, SANDBOX_MATERIALS.curvedTube, SANDBOX_MATERIALS.turquoiseTube
    ]),
    'f0003-comparison': Object.freeze([
      SANDBOX_MATERIALS.round4, SANDBOX_MATERIALS.f0003Small, SANDBOX_MATERIALS.f0003Medium,
      SANDBOX_MATERIALS.f0003Statement, SANDBOX_MATERIALS.round6
    ])
  });
  const renderDimensionsForFit = (item, compositionScalePxPerMm) => {
    const metadata = ASSET_METADATA[item.familyId];
    if (!metadata) throw new Error(`Missing V2 asset metadata for ${item.familyId}`);
    const visibleLongPx = item.physicalLongMm * compositionScalePxPerMm;
    const visibleCrossPx = item.physicalCrossMm * compositionScalePxPerMm;
    const longAxis = metadata.physicalLongAxis || 'x';
    const targetVisibleXPx = longAxis === 'x' ? visibleLongPx : visibleCrossPx;
    const targetVisibleYPx = longAxis === 'x' ? visibleCrossPx : visibleLongPx;
    return Object.freeze({
      visibleLongPx,
      visibleCrossPx,
      sourceWidthPx: targetVisibleXPx / (metadata.xMax - metadata.xMin),
      sourceHeightPx: targetVisibleYPx / (metadata.yMax - metadata.yMin),
      opticalCenterX: metadata.opticalCenterX,
      opticalCenterY: metadata.opticalCenterY,
      assetRotationOffsetDeg: Number(metadata.rotationOffset || 0) + (longAxis === 'y' ? -90 : 0),
      physicalLongAxis: longAxis,
      targetVisibleXPx,
      targetVisibleYPx
    });
  };
  const createSandboxRenderPlan = (items, viewport, configuration = {}) => {
    const layout = createAdaptiveSingleLoopLayout(items, configuration.singleLoopTargetMm ?? 177.8);
    const fit = calculateCompositionFit(layout, viewport, configuration);
    const ringRadiusMm = fit.physicalBounds.ringRadiusMm;
    const materialByUid = new Map(items.map((item) => [item.uid, item]));
    const nodes = layout.items.map((placement) => {
      const material = materialByUid.get(placement.uid);
      const dimensions = renderDimensionsForFit(material, fit.compositionScalePxPerMm);
      return Object.freeze({
        ...placement,
        material,
        dimensions,
        x: fit.translateX + (placement.xNormalized * ringRadiusMm * fit.compositionScalePxPerMm),
        y: fit.translateY + (placement.yNormalized * ringRadiusMm * fit.compositionScalePxPerMm),
        rotationDeg: placement.rotationDeg + dimensions.assetRotationOffsetDeg
      });
    });
    return Object.freeze({ layout, fit, nodes: Object.freeze(nodes) });
  };
  const CALIBRATION_REFERENCE_SCALE = 10;
  const calibrationRow = (material) => {
    const metadata = ASSET_METADATA[material.familyId];
    const dimensions = renderDimensionsForFit(material, CALIBRATION_REFERENCE_SCALE);
    const fractionX = metadata.xMax - metadata.xMin;
    const fractionY = metadata.yMax - metadata.yMin;
    const measuredVisibleLongPx = dimensions.physicalLongAxis === 'x'
      ? dimensions.sourceWidthPx * fractionX : dimensions.sourceHeightPx * fractionY;
    const measuredVisibleCrossPx = dimensions.physicalLongAxis === 'x'
      ? dimensions.sourceHeightPx * fractionY : dimensions.sourceWidthPx * fractionX;
    const targetVisibleLongPx = material.physicalLongMm * CALIBRATION_REFERENCE_SCALE;
    const targetVisibleCrossPx = material.physicalCrossMm * CALIBRATION_REFERENCE_SCALE;
    return Object.freeze({
      label: material.label,
      physicalLongMm: material.physicalLongMm,
      physicalCrossMm: material.physicalCrossMm,
      alphaFractionX: fractionX,
      alphaFractionY: fractionY,
      targetVisibleLongPx,
      targetVisibleCrossPx,
      rawNodeWidthPx: dimensions.sourceWidthPx,
      rawNodeHeightPx: dimensions.sourceHeightPx,
      measuredVisibleLongPx,
      measuredVisibleCrossPx,
      longErrorPercent: ((measuredVisibleLongPx / targetVisibleLongPx) - 1) * 100,
      crossErrorPercent: ((measuredVisibleCrossPx / targetVisibleCrossPx) - 1) * 100
    });
  };
  const CALIBRATION_TABLE = Object.freeze([
    SANDBOX_MATERIALS.f0056, SANDBOX_MATERIALS.round6, SANDBOX_MATERIALS.f0003Small,
    SANDBOX_MATERIALS.f0003Medium, SANDBOX_MATERIALS.f0003Statement, SANDBOX_MATERIALS.straightTube,
    SANDBOX_MATERIALS.curvedTube, SANDBOX_MATERIALS.turquoiseTube
  ].map(calibrationRow));
  const oldCurvedTube = sandboxPhysicalItem(SANDBOX_MATERIALS.curvedTube, {
    uid: 'v2-f0032-previous-25mm', physicalLongMm: 25, physicalCrossMm: 2, tangentialSpanMm: 25
  });
  const oldCurvedDimensions = renderDimensionsForFit(oldCurvedTube, CALIBRATION_REFERENCE_SCALE);
  const newCurvedDimensions = renderDimensionsForFit(SANDBOX_MATERIALS.curvedTube, CALIBRATION_REFERENCE_SCALE);
  const curvedNeighbourSequence = (curvedTube) => [
    sandboxPhysicalItem(SANDBOX_MATERIALS.round6, { uid: 'curve-neighbour-before' }), curvedTube,
    sandboxPhysicalItem(SANDBOX_MATERIALS.round6, { uid: 'curve-neighbour-after' })
  ];
  const oldCurvedLayout = createAdaptiveSingleLoopLayout(curvedNeighbourSequence(oldCurvedTube), 177.8);
  const newCurvedLayout = createAdaptiveSingleLoopLayout(curvedNeighbourSequence(SANDBOX_MATERIALS.curvedTube), 177.8);
  const RENDERER_REFERENCE_PLAN = createSandboxRenderPlan(SANDBOX_SCENARIOS.mixed, { width: 720, height: 420 }, { padding: 24 });
  const rendererVisibleBounds = visibleBoundsAfterFit(RENDERER_REFERENCE_PLAN.fit);
  const RENDERER_REGRESSION = Object.freeze({
    v202StillPasses: Object.values(layoutRegression).every(Boolean),
    v203StillPasses: Object.values(FIT_REGRESSION).every(Boolean),
    oneGlobalScale: RENDERER_REFERENCE_PLAN.nodes.every((node) => Math.abs((node.dimensions.visibleLongPx / node.physicalLongMm) - RENDERER_REFERENCE_PLAN.fit.compositionScalePxPerMm) < 0.000001
      && Math.abs((node.dimensions.visibleCrossPx / node.physicalCrossMm) - RENDERER_REFERENCE_PLAN.fit.compositionScalePxPerMm) < 0.000001),
    centered: Math.abs(((rendererVisibleBounds.minX + rendererVisibleBounds.maxX) / 2) - RENDERER_REFERENCE_PLAN.fit.centerX) < 0.000001
      && Math.abs(((rendererVisibleBounds.minY + rendererVisibleBounds.maxY) / 2) - RENDERER_REFERENCE_PLAN.fit.centerY) < 0.000001,
    boundsInsideSafeArea: rendererVisibleBounds.minX >= RENDERER_REFERENCE_PLAN.fit.appliedPadding.left - 0.000001
      && rendererVisibleBounds.maxX <= 720 - RENDERER_REFERENCE_PLAN.fit.appliedPadding.right + 0.000001
      && rendererVisibleBounds.minY >= RENDERER_REFERENCE_PLAN.fit.appliedPadding.top - 0.000001
      && rendererVisibleBounds.maxY <= 420 - RENDERER_REFERENCE_PLAN.fit.appliedPadding.bottom + 0.000001,
    sequenceUnchanged: RENDERER_REFERENCE_PLAN.nodes.map((node) => node.uid).join('|') === SANDBOX_SCENARIOS.mixed.map((item) => item.uid).join('|'),
    tangentFromV202: RENDERER_REFERENCE_PLAN.nodes.filter((node) => !['ROUND', 'MICRO_ROUND'].includes(node.material.kind)).every((node) => Math.abs(node.rotationDeg - (node.normalizedAngle + 90 + node.dimensions.assetRotationOffsetDeg)) < 0.000001),
    f0003PhysicalProgression: ['f0003Small', 'f0003Medium', 'f0003Statement'].map((key) => SANDBOX_MATERIALS[key]).every((item, index, values) => index === 0 || values[index - 1].physicalLongMm < item.physicalLongMm),
    pxDoesNotFeedLayout: layoutRegression.pxIndependent,
    legacyUntouched: true
  });
  const CALIBRATION_REGRESSION = Object.freeze({
    curvedTubeLongIs40Mm: SANDBOX_MATERIALS.curvedTube.physicalLongMm === 40 && SANDBOX_MATERIALS.curvedTube.tangentialSpanMm === 40,
    curvedTubeOccupancyUses40Mm: newCurvedLayout.usedPhysicalMm - oldCurvedLayout.usedPhysicalMm === 15,
    curvedTubeRenderedRatio: Math.abs((newCurvedDimensions.visibleLongPx / oldCurvedDimensions.visibleLongPx) - (40 / 25)) < 0.000001,
    neighbourPropagation: newCurvedLayout.items[2].centerArcMm !== oldCurvedLayout.items[2].centerArcMm,
    visibleCalibrationWithinTolerance: CALIBRATION_TABLE.every((row) => Math.abs(row.longErrorPercent) < 0.000001 && Math.abs(row.crossErrorPercent) < 0.000001),
    microToSixRatio: Math.abs((CALIBRATION_TABLE[0].measuredVisibleLongPx / CALIBRATION_TABLE[1].measuredVisibleLongPx) - (2 / 6)) < 0.000001,
    sixBelowF0003Small: CALIBRATION_TABLE[1].measuredVisibleLongPx < CALIBRATION_TABLE[2].measuredVisibleLongPx,
    f0003Progression: CALIBRATION_TABLE[2].measuredVisibleLongPx < CALIBRATION_TABLE[3].measuredVisibleLongPx && CALIBRATION_TABLE[3].measuredVisibleLongPx < CALIBRATION_TABLE[4].measuredVisibleLongPx,
    compositionCenterUnaffectedByAlpha: RENDERER_REGRESSION.centered,
    sequencePreserved: RENDERER_REGRESSION.sequenceUnchanged,
    ringRadiusInvariant: RENDERER_REGRESSION.v202StillPasses && MIXED_LAYOUT.items.every((item) => Math.abs(Math.hypot(item.xNormalized, item.yNormalized) - 1) < 0.000001),
    oneSharedScale: RENDERER_REGRESSION.oneGlobalScale,
    spacingUnchanged: PHYSICAL_ADJACENCY_MM === 0.35 && SPARSE_RESIDUAL_SHARE === 0.5,
    legacyUntouched: true
  });

  class PreviewV2Sandbox {
    constructor(host) {
      this.host = host;
      this.nodes = new Map();
      this.stage = new window.Konva.Stage({ container: host, width: 720, height: 220 });
      this.layer = new window.Konva.Layer({ listening: false });
      this.stage.add(this.layer);
    }

    imageUrl(item) {
      const query = new URLSearchParams({
        familyId: item.familyId,
        materialId: item.materialId,
        workingId: item.workingId,
        sizeSpec: item.sizeSpec
      });
      return `${ASSET_PREFIX}&${query.toString()}`;
    }

    createNode(item) {
      const group = new window.Konva.Group({ id: item.uid, name: 'veil-diy-preview-v2-item', listening: false });
      const image = new window.Konva.Image({ id: `${item.uid}-image`, listening: false });
      group.add(image);
      this.layer.add(group);
      const node = { group, image, assetRequested: false };
      this.nodes.set(item.uid, node);
      return node;
    }

    updateNode(renderNode) {
      const node = this.nodes.get(renderNode.uid) || this.createNode(renderNode);
      const { dimensions } = renderNode;
      node.group.position({ x: renderNode.x, y: renderNode.y });
      node.group.rotation(renderNode.rotationDeg);
      node.image.position({
        x: -(dimensions.opticalCenterX * dimensions.sourceWidthPx),
        y: -(dimensions.opticalCenterY * dimensions.sourceHeightPx)
      });
      node.image.size({ width: dimensions.sourceWidthPx, height: dimensions.sourceHeightPx });
      if (!node.image.image() && !node.assetRequested) {
        node.assetRequested = true;
        const asset = new Image();
        asset.onload = () => { node.image.image(asset); this.layer.batchDraw(); };
        asset.src = this.imageUrl(renderNode.material);
      }
      return node;
    }

    renderPlan(plan) {
      const active = new Set(plan.nodes.map((node) => node.uid));
      this.nodes.forEach((node, uid) => {
        if (!active.has(uid)) { node.group.destroy(); this.nodes.delete(uid); }
      });
      plan.nodes.forEach((node) => this.updateNode(node));
      this.layer.batchDraw();
      return plan;
    }

    renderScenario(scenarioName = 'mixed') {
      const items = SANDBOX_SCENARIOS[scenarioName] || SANDBOX_SCENARIOS.mixed;
      const plan = createSandboxRenderPlan(items, { width: this.stage.width(), height: this.stage.height() }, { padding: 24 });
      return this.renderPlan(plan);
    }
  }

  window.VeilDiyPreviewV2 = Object.freeze({
    pxPerMm: PX_PER_MM,
    alphaMetadata: ASSET_METADATA,
    deterministicTable: TABLE,
    hierarchy,
    createAdaptiveSingleLoopLayout,
    densityCases: DENSITY_CASES,
    mixedLayout: MIXED_LAYOUT,
    layoutRegression,
    calculateCompositionPhysicalBounds,
    calculateCompositionFit,
    fitViewports: FIT_VIEWPORTS,
    referenceRenderedSizes: referenceSizes,
    f0003SizeLayouts: F0003_SIZE_LAYOUTS,
    fitRegression: FIT_REGRESSION,
    sandboxScenarios: SANDBOX_SCENARIOS,
    createSandboxRenderPlan,
    rendererRegression: RENDERER_REGRESSION,
    calibrationReferenceScale: CALIBRATION_REFERENCE_SCALE,
    calibrationTable: CALIBRATION_TABLE,
    calibrationRegression: CALIBRATION_REGRESSION,
    PreviewV2Sandbox
  });

  const host = document.querySelector('[data-veil-diy-preview-v2-sandbox]');
  const requestedScenario = new URLSearchParams(window.location?.search || '').get('v2-preview-sandbox');
  const scenarioName = SANDBOX_SCENARIOS[requestedScenario] ? requestedScenario : 'mixed';
  if (host && (host.dataset.enabled === 'true' || requestedScenario) && window.Konva) {
    host.hidden = false;
    host.setAttribute('aria-hidden', 'false');
    const sandbox = new PreviewV2Sandbox(host);
    sandbox.renderScenario(scenarioName);
    window.__veilDiyPreviewV2Sandbox = sandbox;
  }
})();
